import { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLink } from 'react-aria';
import { NavLink } from 'react-router';

import defaultAriaProps from 'client/utils/constants/ariaPropTypes';

import iconButtonStyles from 'client/components/aria/Button/IconButton.module.scss';
import buttonStyles from 'client/components/aria/Button/Button.module.scss';

// https://react-spectrum.adobe.com/react-aria/useLink.html

// eslint-disable-next-line react/display-name
const Link = forwardRef(({ children, variant, className, href, to, target, icon, ...props }, ref) => {
    const defaultRef = useRef();
    const linkRef = ref || defaultRef;

    const styles = icon ? iconButtonStyles : buttonStyles;

    const { linkProps, isPressed } = useLink(props, ref);

    const { isDisabled } = props;

    return (
        to
            ? <NavLink
                data-testid={props['data-testid']}
                ref={linkRef}
                className={[
                    'govuk-button',
                    styles[variant],
                    className,
                    isPressed ? styles.pressed : null,
                    isDisabled ? styles.disabled : null
                ].join(' ')}
                to={isDisabled ? null : to}
                target={target}
                {...linkProps}
            >
                {children}
            </NavLink>
            : <a
                data-testid={props['data-testid']}
                ref={linkRef}
                className={[
                    'govuk-button',
                    styles[variant],
                    className,
                    isPressed ? styles.pressed : null,
                    isDisabled ? styles.disabled : null
                ].join(' ')}
                href={isDisabled ? '#!' : href}
                target={target}
                {...linkProps}
            >
                {children}
            </a>
    );
});

Link.propTypes = {
    ...defaultAriaProps,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'warning', 'success', 'info', 'transparent']),
    href: PropTypes.string,
    to: PropTypes.string,
    target: PropTypes.string,
    isDisabled: PropTypes.bool,
    icon: PropTypes.bool
};

Link.defaultProps = {
    variant: 'default'
};

export default Link;
